import React from 'react';
import { useIntl } from 'react-intl';

import {
  IntlErrorMessageDefinerId,
  IntlErrorMessageFormatted,
  RawIntlErrorMessages,
} from '../../types/ApiErrorCodes';
import { ErrorPresentational } from '../../types/common';

export type UseErrorMessagesIntlParams = {
  ids: IntlErrorMessageDefinerId[];
  messages: RawIntlErrorMessages;
};

type UseFindErrorParams = {
  apiErrorMessage: ErrorPresentational | null;
  messages: IntlErrorMessageFormatted[];
};

export const errorHooksIntl = {
  useFindApiErrorTranslation() {
    const findApiErrorTranslation = React.useCallback(({
      apiErrorMessage,
      messages,
    }: UseFindErrorParams): IntlErrorMessageFormatted | null => {
      if (apiErrorMessage === null) {
        return null;
      }

      const filteredByCode: IntlErrorMessageFormatted[] =
        messages.filter(item => item.code === apiErrorMessage.code);

      const foundByReferencedRequestParameter: IntlErrorMessageFormatted | undefined =
        filteredByCode.find(
          item => item.referencedRequestParameter === apiErrorMessage.referencedRequestParameter,
        );

      if (foundByReferencedRequestParameter) {
        return foundByReferencedRequestParameter;
      }

      if (filteredByCode.length !== 0) {
        return filteredByCode[0];
      }

      return null;
    }, []);

    return { findApiErrorTranslation };
  },

  useErrorMessagesIntl({
    ids,
    messages,
  }: UseErrorMessagesIntlParams): IntlErrorMessageFormatted[] {
    const intl = useIntl();

    return React.useMemo((): IntlErrorMessageFormatted[] => ids.map(item => ({
      referencedRequestParameter: item.referencedRequestParameter,
      code: item.code,
      intlMessageFormatted: intl.formatMessage(messages[item.intlMessageId]),
    })), [ids, intl, messages]);
  },

  useTranslateErrorCallback(params: UseErrorMessagesIntlParams) {
    const { findApiErrorTranslation } = errorHooksIntl.useFindApiErrorTranslation();

    const errorMessagesIntl = errorHooksIntl.useErrorMessagesIntl(params);

    const translateErrorCallback = React.useCallback((
      apiErrorMessage: ErrorPresentational | null,
    ): IntlErrorMessageFormatted | null => {
      return findApiErrorTranslation({
        apiErrorMessage,
        messages: errorMessagesIntl,
      });
    }, [errorMessagesIntl, findApiErrorTranslation]);

    return { translateErrorCallback };
  },
};
